import { Show, SignUp } from '@clerk/react'
import type * as React from 'react'
import { useEffect, useState } from 'react'
import { Link } from '#router'
import { Button } from '@/components/ui/button'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import type { AuditionAnswers } from '@/lib/audition/answers'
import {
  forgetStoredApplyToken,
  readStoredApplyToken,
} from '@/lib/audition/apply-token'
import { isDraftExpiredError } from '@/lib/audition/resubmit'
import { applyResumePath } from '@/lib/audition/resume-url'
import { FAST_LANE_FLAG } from '@/lib/feature-flags'
import { trpc } from '@/lib/trpc'
import LoadingFallback from '../loading-fallback'
import { BoostHub } from './boost-hub'
import { LaneChoice } from './lane-choice'

/**
 * How long the done screen waits for `enable-fast-lane` before deciding the
 * flag is unreadable. Long enough that a normal PostHog round-trip wins,
 * short enough that a blocked one is not a dead page.
 */
const FLAG_WAIT_MS = 4_000

function formatAppliedDate(appliedAt: string | Date): string {
  return new Date(appliedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export type DoneScreenProps = (
  | { variant: 'justSubmitted' }
  | {
      variant: 'returning'
      appliedAt: string | Date
      /**
       * Set once the applicant has re-entered the review queue via resubmit.
       * When present, the returning copy also acknowledges the resubmission
       * so the screen doesn't read as if we ignored their latest action.
       */
      resubmittedAt?: string | Date | null
    }
) & {
  /** Returning-visitor resubmit / start-new controls, when eligible. */
  actions?: React.ReactNode
}

export function DoneScreen(props: DoneScreenProps) {
  const spendsToken = props.variant === 'justSubmitted'
  useEffect(() => {
    // Only a fresh completion ends the token's life. The returning variant
    // renders while the wizard is still USING the token for the pick-a-time
    // and enrollment surfaces — clearing it there logged people out of their
    // own booking link on the next refresh.
    if (spendsToken) forgetStoredApplyToken()
  }, [spendsToken])

  return (
    <section className="space-y-4">
      {props.variant === 'returning' ? (
        // Base copy is verbatim per spec (only the date interpolates); the
        // resubmission line below is appended conditionally so a returning
        // applicant who just resubmitted sees us acknowledge it.
        <>
          <p className="text-muted-foreground">
            {`Looks like you submitted an application on ${formatAppliedDate(props.appliedAt)}. Thanks so much! We review every application - keep an eye on your inbox for our response! For now, there's nothing left to do except keep being awesome 😉`}
          </p>
          {props.resubmittedAt && (
            <p className="text-muted-foreground">
              {`We also have received a resubmission from you on ${formatAppliedDate(props.resubmittedAt)} - your enthusiasm is legendary, thanks so much! Your application has been moved to the front of our queue. Stay tuned for a followup!`}
            </p>
          )}
        </>
      ) : (
        <>
          <h1 className="text-2xl font-semibold">Application received 🎉</h1>
          <p className="text-muted-foreground">
            A real human on our team reads every application — no bots, no
            keyword filters. Sit tight and we'll be in touch by email. Nothing
            else for you to do right now.
          </p>
        </>
      )}
      {props.actions}
      <Button asChild variant="outline">
        <Link href="/">Back to the site</Link>
      </Button>
    </section>
  )
}

/**
 * Signed-in branch of the final wizard step. An active Clerk session means
 * `<SignUp>` never mounts, so the `user.created` webhook that normally links
 * the submission (lib/inngest/functions/link-audition-signup.ts) never
 * fires. This confirms the link server-side via `audition.linkToAccount`
 * before ever showing "Application received" — the done screen only mounts
 * on confirmed success, never optimistically.
 */
function LinkAndDone({
  token,
  onExpired,
  answers,
}: {
  token: string
  onExpired: () => void
  answers: AuditionAnswers
}) {
  const linkToAccount = trpc.audition.linkToAccount.useMutation()
  const { mutate, isError, isSuccess, error } = linkToAccount
  const fastLane = useFeatureFlag(FAST_LANE_FLAG)
  // The draft behind the token passed its window while the tab sat open:
  // `loadSubmission` refuses the link before it starts, and would refuse
  // every retry the same way. Hand the applicant back to the wizard's fresh
  // start rather than offer a Try again button that can never succeed.
  const expired = isError && isDraftExpiredError(error)
  useEffect(() => {
    if (expired) onExpired()
  }, [expired, onExpired])
  // PostHog can be blocked outright — an ad blocker or a corporate proxy on
  // the `/ingest` host — and then `isFeatureEnabled` answers `undefined`,
  // `onFeatureFlags` never fires, and `isLoaded` stays false for the life of
  // the page. The gate below needs a floor or that applicant sits on a
  // spinner forever instead of seeing the screen they saw before this flag
  // existed. Past the floor an unreadable flag counts as off, which is what
  // every applicant sees today.
  const [flagTimedOut, setFlagTimedOut] = useState(false)
  useEffect(() => {
    if (fastLane.isLoaded) return
    const timer = setTimeout(() => setFlagTimedOut(true), FLAG_WAIT_MS)
    return () => clearTimeout(timer)
  }, [fastLane.isLoaded])

  // Which screen the link succeeds INTO, decided once and then frozen.
  // `useFeatureFlag` keeps answering `onFeatureFlags`, and PostHog
  // re-evaluates on identify — which is exactly what the Clerk sign-in
  // behind this link triggers. An unfrozen read lets a percentage rollout
  // land on the other side of that identify and either unmount a lane flow
  // mid-payment, or mount one after DoneScreen already spent the resume
  // token its payment and booking calls are addressed by.
  //
  // A loaded flag beats the floor: the floor exists so nobody waits on a
  // spinner forever, and until the link succeeds nothing has been spent, so
  // an answer that arrives after the floor but before this point is still
  // worth honouring. Once frozen, a late flag changes nothing.
  const pending =
    !isSuccess || (!fastLane.isLoaded && !flagTimedOut)
      ? null
      : fastLane.isLoaded && fastLane.enabled
  const [decided, setDecided] = useState<boolean | null>(null)
  useEffect(() => {
    if (pending === null) return
    setDecided((prev) => prev ?? pending)
  }, [pending])
  const fastLaneChosen = decided ?? pending

  // Deliberately NOT latched against StrictMode's double effect: the dev
  // remount cancels the first call's transport (tRPC aborts on
  // unsubscribe), so a latch leaves the surviving instance idle on a
  // spinner forever. Double-firing is safe — the server answers a lost
  // same-user race with alreadyLinked success, so both orders converge on
  // the done screen.
  useEffect(() => {
    mutate({ token })
  }, [token, mutate])

  if (isSuccess) {
    // Wait for the flag — or the floor — before rendering either outcome.
    // DoneScreen SPENDS the resume token on mount, so letting it render for
    // a single frame while the flag is still loading destroys the token the
    // lane page's payment and booking calls are addressed by.
    if (fastLaneChosen === null) return <LoadingFallback />
    // The lane page replaces the done screen only once enable-fast-lane is
    // on AND loaded, so the live consideration-lane flow is byte-for-byte
    // unchanged and nobody sees a price flash in and vanish. It keeps the
    // token alive too: its payment and booking calls are token-addressed.
    if (fastLaneChosen) {
      return <LaneChoice token={token} answers={answers} />
    }
    return (
      <DoneScreen
        variant="justSubmitted"
        actions={<BoostHub address={{ token }} />}
      />
    )
  }

  if (expired) return <LoadingFallback />

  if (isError) {
    // CONFLICT means the server-side stage guard already rejected this
    // submission (already linked/advanced, or lost a concurrent-link race)
    // — the request already happened, so retrying can only ever fail the
    // same way. Show the terminal message with no retry button.
    if (error?.data?.code === 'CONFLICT') {
      return (
        <section className="space-y-4">
          <p className="text-muted-foreground">
            This application was already submitted.
          </p>
        </section>
      )
    }

    return (
      <section className="space-y-4">
        <p className="text-destructive">
          We couldn't finish submitting your application — please try again.
        </p>
        <Button onClick={() => mutate({ token })}>Try again</Button>
      </section>
    )
  }

  return <LoadingFallback />
}

export function AccountStep({
  token,
  contact,
  onExpired,
  answers,
}: {
  token: string | null
  contact: { email?: string; firstName?: string; lastName?: string }
  /** The signed-in link was refused because the unfinished draft expired. */
  onExpired: () => void
  /** What they just sent, for the lane page's "Review my answers". */
  answers: AuditionAnswers
}) {
  // Defensive: the wizard only mounts this step once a token exists, but
  // guard against a null token reaching here anyway rather than rendering a
  // broken SignUp/mutation.
  if (!token) return null

  // Clerk's redirect after signup is a full page load, and the wizard
  // re-reads the token from storage on mount. When storage didn't keep it
  // (blocked, quota, private mode) the URL is the only carrier that survives
  // the reload — the emailed resume link's shape, and the wizard strips it
  // again on mount. A read-back rather than "did the write throw":
  // what matters is whether a reload will find THIS token.
  const redirectUrl =
    readStoredApplyToken() === token ? '/apply' : applyResumePath(token)

  return (
    <>
      <Show when="signed-out">
        <section className="space-y-4">
          <h1 className="text-2xl font-semibold">
            Last step — create your account
          </h1>
          {/* The design canvas framed this as "Nothing you have answered so
              far is saved yet. Signing in saves it." That is false —
              `audition.start` created the row and minted a resume token back
              on the contact step. The account is what SUBMITS the
              application and what links it to a login, so that is what the
              copy promises. */}
          <p className="text-muted-foreground">
            Your answers are already saved. Creating an account submits your
            application, and it is how you get back in later.
          </p>
          {/* The SignUp widget's own "Already have an account?" footer is
              hidden because it links to a /sign-in route this app does not
              have. Without this line an existing customer has no visible
              route at all — and the route is a LINK, not a second signup. */}
          <p className="text-muted-foreground text-sm">
            Already have an account with us? Sign in from the top of the page
            and we'll link this application to it.
          </p>
          {/* No `routing`/`path`: Core 3 defaults to virtual routing (the old
              explicit `routing="virtual"`), keeping the widget URL-less inside
              the wizard. */}
          <SignUp
            initialValues={{
              emailAddress: contact.email,
              firstName: contact.firstName,
              lastName: contact.lastName,
            }}
            unsafeMetadata={{ auditionToken: token }}
            forceRedirectUrl={redirectUrl}
            // The footer's "Already have an account? Sign in" link navigates
            // to a /sign-in route this app doesn't have (dead 404). Hide it;
            // an existing-account applicant signs in via the header modal,
            // which keeps the wizard mounted.
            appearance={{ elements: { footerAction: { display: 'none' } } }}
          />
        </section>
      </Show>
      <Show when="signed-in">
        <LinkAndDone token={token} onExpired={onExpired} answers={answers} />
      </Show>
    </>
  )
}
